import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import {
  SelectDeliveryStoreStatus,
  useSelectDeliveryStore,
} from 'features/store/hooks/use-select-delivery-store';
import { useSelectStore } from 'features/store/hooks/use-select-store';
import { useStoreUtils } from 'features/store/hooks/use-store-utils';
import { addressReducer } from 'features/store/pages/delivery-store-selector/reducer';
import { ActionStatus } from 'features/store/pages/delivery-store-selector/types';

import { clearAllLocalDataForEnvSwitch } from 'components/env-picker';
import { ServiceMode } from 'generated/graphql-gateway';
import { MAPS_API_KEY, MAPS_HEADERS } from 'hooks/geolocation/constants';
import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { getStaticPathname } from 'navigation/location/location';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import {
  RBIEnv,
  env,
  getApiKey,
  getConfigValue,
  region,
  setPickedEnvConfig,
} from 'utils/environment';
import { Region } from 'utils/environment/types';
import { IPlaceAddress, parsePlaceAddress } from 'utils/geolocation';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

// Extracted helpers to make the logic easier to test and maintain
export type ServiceModeParam = 'pickup' | 'delivery' | undefined;

export interface AutoSelectParams {
  storeId?: string;
  envParam?: string;
  regionParam?: string;
  menuItem?: string;
  loyaltyEngineId?: string;
  menuItemType?: string;
  serviceMode?: ServiceModeParam;
  // Delivery params
  address?: string;
  phoneNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  alias?: string;
  defaultInstruction?: string;
  placeId?: string;
}

export type EnvRegionDecision =
  | { kind: 'unchanged' }
  | { kind: 'invalid' }
  | { kind: 'changed'; newEnv: RBIEnv; newRegion: Region };

export function isNoAutoSelectParams(params: AutoSelectParams): boolean {
  return (
    !params.storeId &&
    !params.envParam &&
    !params.regionParam &&
    !params.menuItem &&
    !params.menuItemType &&
    !params.serviceMode &&
    // If any delivery-specific params are present, we do have params
    !params.address &&
    !params.phoneNumber &&
    !params.addressLine1 &&
    !params.city &&
    !params.zip &&
    !params.placeId &&
    !params.loyaltyEngineId
  );
}

export function evaluateEnvRegionSelection(
  allowEnvPicker: boolean,
  envParam: string | undefined,
  regionParam: string | undefined,
  currentEnv: RBIEnv,
  currentRegion: Region
): EnvRegionDecision {
  if (!allowEnvPicker || (!envParam && !regionParam)) {
    return { kind: 'unchanged' };
  }

  if (envParam && !Object.values(RBIEnv).includes(envParam as RBIEnv)) {
    return { kind: 'invalid' };
  }

  const newEnv = (envParam as RBIEnv) || currentEnv;
  const newRegion = (regionParam as Region) || currentRegion;

  if (newEnv === currentEnv && newRegion === currentRegion) {
    return { kind: 'unchanged' };
  }

  return { kind: 'changed', newEnv, newRegion };
}

export function computeMenuRoute({
  loyaltyEngineId,
  menuItem,
  menuItemType,
}: {
  loyaltyEngineId?: string;
  menuItem?: string;
  menuItemType?: string;
}): string | null {
  if (menuItem && menuItemType) {
    if (menuItemType === 'offer') {
      logger.info({
        message: '[useAutoSelectUrl][menu]: Redirecting to offer:',
        context: {
          loyaltyEngineId,
          menuItem,
          menuItemType,
          url: `${routes.offers}/${menuItem}?loyaltyEngineId=${loyaltyEngineId}`,
        },
      });
      return `${routes.offers}/${menuItem}?loyaltyEngineId=${loyaltyEngineId}`;
    }
    if (menuItemType === 'reward') {
      logger.info({
        message: '[useAutoSelectUrl][menu]: Redirecting to item:',
        context: {
          menuItem,
          menuItemType,
          url: `${routes.rewards}/${menuItem}?rewardId=${menuItem}`,
        },
      });
      return `${routes.rewards}/${menuItem}?rewardId=${menuItem}`;
    }
    return `${routes.menu}/${menuItemType}/${menuItem}`;
  }
  return null;
}

export function isStoreAlreadySelected(
  storeId: string | undefined,
  serviceModeParam: ServiceModeParam,
  currentStoreNumber: string,
  selectedServiceMode: ServiceMode
): boolean {
  if (!storeId) {
    return false;
  }

  // If service mode not provided, just compare store ids
  if (!serviceModeParam) {
    return storeId === currentStoreNumber;
  }

  // With service mode, both store and mode must match
  const desiredMode =
    serviceModeParam === 'delivery' ? ServiceMode.DELIVERY : ServiceMode.DRIVE_THRU;
  return storeId === currentStoreNumber && selectedServiceMode === desiredMode;
}

type MinimalStore = {
  physicalAddress?: IPlaceAddress;
  phoneNumber?: string | null;
};

export interface StoreSelectionDeps {
  fetchStore: (storeId: string) => Promise<any>;
  selectStore: (args: any) => any;
  onDeliveryAddressPending: (payload: { address: IPlaceAddress; phoneNumber: string }) => void;
}

export async function fetchAndSelectStore(
  deps: StoreSelectionDeps,
  storeId: string,
  serviceModeParam: ServiceModeParam
): Promise<'handled' | 'redirectBase'> {
  logger.info({
    message: '[useAutoSelectUrl][storeSelection]: Fetching store:',
    context: { storeId },
  });

  const newStore = (await deps.fetchStore(storeId)) as MinimalStore | null | undefined;
  if (!newStore) {
    logger.info({
      message: '[useAutoSelectUrl][storeSelection]: No store returned from fetchStore',
    });
    return 'redirectBase';
  }

  if (serviceModeParam === 'delivery') {
    deps.onDeliveryAddressPending({
      address: newStore.physicalAddress as IPlaceAddress,
      phoneNumber: newStore.phoneNumber || '',
    });
  }

  logger.info({
    message: '[useAutoSelectUrl][storeSelection]: Store fetched, selecting store...',
  });

  const requestedServiceMode =
    serviceModeParam === 'delivery' ? ServiceMode.DELIVERY : ServiceMode.DRIVE_THRU;

  await deps.selectStore({ newStore, requestedServiceMode });

  logger.info({
    message: '[useAutoSelectUrl][storeSelection]: Store selected successfully:',
    context: { storeId },
  });

  return 'handled';
}

// Note this hook only works for web for now
export const useAutoSelectUrl = () => {
  const params = useLocalSearchParams();
  const router = useRouter();
  const { isFetchingStore, store, isSelectingStore } = useStoreContext();
  const { serviceMode: selectedServiceMode } = useServiceModeContext();
  const { libraryLoaded, demandLibrary } = useGoogleGeolocationLibrary();
  const { fetchStore } = useStoreUtils({
    store,
  });
  const selectStore = useSelectStore();
  const selectDeliveryStore = useSelectDeliveryStore();
  const { setOrderPhoneNumber } = useOrderContext();
  const [isNavigationReady, setIsNavigationReady] = useState(false);
  const [hasAttemptedNavigation, setHasAttemptedNavigation] = useState(false);
  const allowEnvPicker = getConfigValue({
    key: 'allowEnvPicker',
    disallowEnvPickerValue: true,
  });
  const [, dispatch] = useReducer(addressReducer, {
    error: null,
    address: null,
    status: ActionStatus.IDLE,
  });
  const didAutoRunRef = useRef(false);

  const handleAutoSelect = useCallback(async () => {
    if (hasAttemptedNavigation || !isNavigationReady || isFetchingStore || isSelectingStore) {
      logger.info('[useAutoSelectUrl]: Navigation not ready, skipping');
      return;
    }

    const storeId = params.storeId as string | undefined;
    const envParam = params.env as string | undefined;
    const regionParam = params.region as string | undefined;
    const menuItem = params.menuItem as string | undefined;
    const menuItemType = params.menuItemType as string | undefined;
    const serviceMode = params.serviceMode as ServiceModeParam;
    const addressText = params.address as string | undefined;
    const phoneNumberParam = params.phoneNumber as string | undefined;
    const addressLine1 = params.addressLine1 as string | undefined;
    const addressLine2 = params.addressLine2 as string | undefined;
    const city = params.city as string | undefined;
    const stateCode = params.state as string | undefined;
    const zip = params.zip as string | undefined;
    const country = params.country as string | undefined;
    const alias = (params.alias as string | undefined) || '';
    const defaultInstruction = params.defaultInstruction as string | undefined;
    const placeId = params.placeId as string | undefined;

    const redirect = (path: string) => {
      if (getStaticPathname() === path) {
        return true; // handled: already at destination
      }
      setHasAttemptedNavigation(true);
      router.replace(path);
      return true;
    };

    const buildPlaceAddress = (): IPlaceAddress => {
      if (addressText && !addressLine1) {
        logger.info({
          message: '[useAutoSelectUrl][deliverySelection]: Building address from single param',
        });
      }
      return {
        addressLine1: addressLine1 || addressText || '',
        addressLine2: addressLine2 || '',
        city: city || '',
        state: stateCode || '',
        zip: zip || '',
        country: country || '',
        defaultInstruction,
      };
    };

    const geocodeAddressText = async (
      input: string
    ): Promise<{ address: IPlaceAddress; lat: number; lng: number } | null> => {
      if (!input) {
        return null;
      }
      try {
        if (!libraryLoaded) {
          await demandLibrary();
        }
        if (window.google?.maps && window.google?.maps?.Geocoder) {
          return await new Promise(resolve => {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address: input }, (results: any, status: any) => {
              if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) {
                return resolve(null);
              }
              const first = results[0];
              const parsed = parsePlaceAddress(first.address_components, { useISOCountry: true });
              const lat = first.geometry?.location?.lat?.();
              const lng = first.geometry?.location?.lng?.();
              if (typeof lat !== 'number' || typeof lng !== 'number') {
                return resolve(null);
              }
              resolve({ address: parsed, lat, lng });
            });
          });
        }
        const apiKey =
          MAPS_API_KEY || getApiKey({ key: 'googleMapsWeb', disallowEnvPickerValue: true });
        if (!apiKey) {
          return null;
        }
        const resp = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            input
          )}&key=${apiKey}`,
          { headers: MAPS_HEADERS }
        );
        if (!resp.ok) {
          return null;
        }
        const json = await resp.json();
        const first = json?.results?.[0];
        if (!first) {
          return null;
        }
        const parsed = parsePlaceAddress(first.address_components, { useISOCountry: true });
        const lat = first.geometry?.location?.lat;
        const lng = first.geometry?.location?.lng;
        if (typeof lat !== 'number' || typeof lng !== 'number') {
          return null;
        }
        return { address: parsed, lat, lng };
      } catch (e) {
        logger.error({
          message: '[useAutoSelectUrl][deliverySelection]: Geocode failed',
          error: e,
        });
        return null;
      }
    };

    const handleNoParams = (): boolean => {
      if (
        isNoAutoSelectParams({
          storeId,
          envParam,
          regionParam,
          menuItem,
          menuItemType,
          serviceMode,
        })
      ) {
        logger.info({ message: '[useAutoSelectUrl]: No parameters, redirecting to base' });
        return redirect(routes.base);
      }
      return false;
    };

    const handleEnvRegion = async (): Promise<boolean> => {
      const decision = evaluateEnvRegionSelection(
        allowEnvPicker,
        envParam,
        regionParam,
        env(),
        region
      );
      if (decision.kind === 'invalid') {
        logger.info({
          message:
            '[useAutoSelectUrl][envPicker]: Invalid environment parameter, redirecting to base',
        });
        return redirect(routes.base);
      }
      if (decision.kind === 'changed') {
        logger.info({
          message: '[useAutoSelectUrl][envPicker]: Environment/region changed clearing local data',
        });
        try {
          await clearAllLocalDataForEnvSwitch();
        } finally {
          logger.info({ message: '[useAutoSelectUrl][envPicker]: setting new config' });
          setPickedEnvConfig({ env: decision.newEnv, buildRegion: decision.newRegion });
          logger.info({ message: '[useAutoSelectUrl][envPicker]: reloading app' });
          window.location.reload();
          return true;
        }
      }
      return false;
    };

    const handleDeliverySelection = async (): Promise<boolean> => {
      const hasDeliveryParams =
        serviceMode === 'delivery' && !!phoneNumberParam && (!!addressLine1 || !!addressText);
      if (!hasDeliveryParams) {
        return false;
      }
      try {
        let deliveryAddress: IPlaceAddress = buildPlaceAddress();
        let coordLat: number | undefined = undefined;
        let coordLng: number | undefined = undefined;
        if (addressText && !addressLine1) {
          const geocoded = await geocodeAddressText(addressText);
          if (geocoded) {
            deliveryAddress = { ...deliveryAddress, ...geocoded.address };
            coordLat = geocoded.lat;
            coordLng = geocoded.lng;
          } else {
            logger.info({
              message: '[useAutoSelectUrl][deliverySelection]: Address geocode not found',
            });
          }
        } else if (!placeId) {
          const parts = [addressLine1, city, stateCode, zip, country].filter(Boolean).join(', ');
          if (parts) {
            const geocoded = await geocodeAddressText(parts);
            if (geocoded) {
              deliveryAddress = {
                addressLine1: deliveryAddress.addressLine1 || geocoded.address.addressLine1,
                addressLine2: deliveryAddress.addressLine2 || geocoded.address.addressLine2,
                city: deliveryAddress.city || geocoded.address.city,
                state: deliveryAddress.state || geocoded.address.state,
                zip: deliveryAddress.zip || geocoded.address.zip,
                country: deliveryAddress.country || geocoded.address.country,
                defaultInstruction: deliveryAddress.defaultInstruction,
              } as IPlaceAddress;
              coordLat = geocoded.lat;
              coordLng = geocoded.lng;
            } else {
              logger.info({
                message: '[useAutoSelectUrl][deliverySelection]: Address geocode not found',
              });
            }
          }
        }

        dispatch({
          type: ActionStatus.PENDING,
          address: {
            address: deliveryAddress,
            phoneNumber: phoneNumberParam,
            placeId,
            coordinates:
              typeof coordLat === 'number' && typeof coordLng === 'number'
                ? { lat: coordLat, lng: coordLng }
                : undefined,
          },
        });

        const { status, restaurant, phoneNumber } = await selectDeliveryStore({
          address: deliveryAddress,
          phoneNumber: phoneNumberParam,
          placeId,
          alias,
          coordinates:
            typeof coordLat === 'number' && typeof coordLng === 'number'
              ? { lat: coordLat, lng: coordLng }
              : undefined,
          isNewAddress: true,
        } as any);

        if (status === SelectDeliveryStoreStatus.QUOTE_ERROR) {
          logger.info({
            message: '[useAutoSelectUrl][deliverySelection]: Quote error for delivery address',
          });
          return redirect(routes.base);
        }
        if (status === SelectDeliveryStoreStatus.QUOTE_UNAVAILABLE) {
          logger.info({
            message: '[useAutoSelectUrl][deliverySelection]: Delivery unavailable for address',
          });
          return redirect(routes.base);
        }
        if (status === SelectDeliveryStoreStatus.UNRECOGNIZED_ADDRESS) {
          logger.info({
            message: '[useAutoSelectUrl][deliverySelection]: Unrecognized delivery address',
          });
          return redirect(routes.base);
        }

        if (status === SelectDeliveryStoreStatus.QUOTE_SUCCESSFUL && restaurant) {
          await selectStore({
            newStore: restaurant,
            requestedServiceMode: ServiceMode.DELIVERY,
            callback: () => setOrderPhoneNumber(phoneNumber ?? phoneNumberParam ?? ''),
          });
          dispatch({ type: ActionStatus.SUCCESS });
          if (!menuItem && !menuItemType) {
            return redirect(routes.menu);
          }
          return false; // handled but continue to navigation
        }
      } catch (error) {
        logger.error({
          message: '[useAutoSelectUrl][deliverySelection]: Failed to select delivery store',
          context: { error },
        });
        return redirect(routes.base);
      }
      return false;
    };

    const handleStoreSelection = async (): Promise<boolean> => {
      if (!storeId) {
        return false;
      }
      const alreadySelected = isStoreAlreadySelected(
        storeId,
        serviceMode,
        store.number ?? '',
        (selectedServiceMode as ServiceMode) ?? ServiceMode.DRIVE_THRU
      );
      if (alreadySelected) {
        logger.info({
          message: '[useAutoSelectUrl][storeSelection]: Store is already selected',
          context: { storeId },
        });
        return false;
      }
      try {
        const result = await fetchAndSelectStore(
          {
            fetchStore,
            selectStore,
            onDeliveryAddressPending: ({ address, phoneNumber }) =>
              dispatch({ type: ActionStatus.PENDING, address: { address, phoneNumber } }),
          },
          storeId,
          serviceMode
        );
        if (result === 'redirectBase') {
          return redirect(routes.base);
        }
        return false;
      } catch (error) {
        logger.error({
          message: '[useAutoSelectUrl][storeSelection]: Failed to fetch/select store:',
          context: { error },
        });
        return redirect(routes.base);
      }
    };

    const handleMenuNavigation = (): boolean => {
      const menuRoute = computeMenuRoute({
        loyaltyEngineId: params.loyaltyEngineId as string | undefined,
        menuItem,
        menuItemType,
      });
      if (menuRoute) {
        logger.info({
          message: '[useAutoSelectUrl][menu]: Redirecting to menu item:',
          context: { menuRoute },
        });
        return redirect(menuRoute);
      }
      return false;
    };

    logger.info({
      message: '[useAutoSelectUrl]: Starting with params:',
      context: { storeId, envParam, regionParam, menuItem, menuItemType, serviceMode },
    });

    if (handleNoParams()) {
      return;
    }
    if (await handleEnvRegion()) {
      return;
    }

    let didSelectStore = false;
    const deliveryRedirected = await handleDeliverySelection();
    if (deliveryRedirected) {
      return;
    }
    // If store is already selected or gets selected, mark it
    if (
      storeId &&
      isStoreAlreadySelected(
        storeId,
        serviceMode,
        store.number ?? '',
        (selectedServiceMode as ServiceMode) ?? ServiceMode.DRIVE_THRU
      )
    ) {
      didSelectStore = true;
    }
    const storeRedirected = await handleStoreSelection();
    if (storeRedirected) {
      return;
    }
    // If we attempted selection by id, consider the store selected at this point
    if (storeId) {
      didSelectStore = true;
    }

    if (handleMenuNavigation()) {
      return;
    }

    if (didSelectStore && !menuItem && !menuItemType) {
      logger.info({ message: '[useAutoSelectUrl][menu]: Store selected, redirecting to menu' });
      if (redirect(routes.menu)) {
        return;
      }
    }

    logger.info({ message: '[useAutoSelectUrl][fallback]: Redirecting to base' });
    redirect(routes.base);
  }, [
    hasAttemptedNavigation,
    isNavigationReady,
    isFetchingStore,
    isSelectingStore,
    libraryLoaded,
    params,
    allowEnvPicker,
    router,
    store.number,
    selectedServiceMode,
    fetchStore,
    selectStore,
    selectDeliveryStore,
    setOrderPhoneNumber,
    demandLibrary,
  ]);

  const hasParams = useMemo(() => {
    return (
      params.storeId ||
      params.env ||
      params.region ||
      params.menuItem ||
      params.menuItemType ||
      params.serviceMode ||
      // Delivery-specific
      params.address ||
      params.phoneNumber ||
      params.addressLine1 ||
      params.city ||
      params.zip ||
      params.placeId
    );
  }, [
    params.storeId,
    params.env,
    params.region,
    params.menuItem,
    params.menuItemType,
    params.serviceMode,
    params.address,
    params.phoneNumber,
    params.addressLine1,
    params.city,
    params.zip,
    params.placeId,
  ]);

  // Auto-execute when the hook is mounted and params are available
  useEffect(() => {
    if (!hasParams || hasAttemptedNavigation || didAutoRunRef.current) {
      return;
    }
    if (!isNavigationReady || isFetchingStore || isSelectingStore) {
      return; // wait until ready, do not set the ref yet
    }
    didAutoRunRef.current = true;
    handleAutoSelect();
  }, [
    handleAutoSelect,
    hasParams,
    hasAttemptedNavigation,
    isNavigationReady,
    isFetchingStore,
    isSelectingStore,
  ]);

  // Ensure navigation is ready before attempting to navigate
  useEffect(() => {
    // Use a small delay to ensure the navigation system is fully initialized
    const timer = setTimeout(() => {
      setIsNavigationReady(true);
    }, 250);

    return () => clearTimeout(timer);
  }, []);

  return {
    handleAutoSelect,
    params,
  };
};
